import { ChangeDetectorRef, Component, HostListener, Inject, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Company } from '../shared/models/company';
import { Person } from '../shared/models/Person';
import { Application } from '../shared/models/Application';
import { firstValueFrom } from 'rxjs';
import { CustomSnackbarComponent } from '../custom-snackbar/custom-snackbar.component';
import { UploadDocumentService } from '../shared/services/upload-document.service';
import { CustomSnackbarData } from '../shared/models/custom-snackbar-data';
import { Contact, returnValueFromDocumentList } from '../shared/models/conflict-result';
import { ContactVerificationResult } from '../shared/models/contact-verification-result';
import { IdVerificationService } from '../shared/services/id-verification.service';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectChange } from '@angular/material/select';
import { FormGroup } from '@angular/forms';
import { IdVerificationResponse } from '../shared/models/id-verification-response';
import { RuleOutcome } from '../shared/models/onboarding-response';
import { DocumentListComponent } from '../document-list/document-list.component';
import { ActionDocuments } from '../shared/models/action-documents';
import { SnackbarService } from '../shared/services/snackbar.service';
import { CountryFieldRequirementService } from '../shared/services/country-field-requirement.service';
import { CountryFieldValidationService } from '../shared/services/country-field-validation.service';
import { CitizenshipCode, CountryFieldRequirement } from '../shared/models/country-field-requirement';

@Component({
    selector: 'xp-verification-result',
    templateUrl: './verification-result.component.html',
    styleUrl: './verification-result.component.scss'
})
export class VerificationResultComponent implements OnInit {
    readonly dialog = inject(MatDialog);
    contactPanelOpenState = false;
    panelOpenState = false;
    displayedColumns: string[] = ['Name', 'Outcome', 'Score'];
    uboColumns: string[] = ['Contact name', 'Type', 'Requirements', 'Dob', 'Phone', 'Email', 'Address', 'Company Reg', 'Ubo Percentage', 'Role', 'Documents', 'Proof of Address', 'Action'];
    downloadPdfBtn: boolean = false;
    isVerifyDisabled: boolean = false;
    verificationResponse?: IdVerificationResponse;
    showProgressBar: boolean = false;
    isUpdateAddress: number | null = null;
    isUpdateDob: number | null = null;
    isUpdateEmail: number | null = null;
    editMode: boolean[] = [];
    isDobChecked: boolean = false;
    isEmailChecked: boolean = false;
    isAddressChecked: boolean = false;
    streetAddress: string | null = '';
    element: Contact | null = null;
    emailError: string[] = [];
    roleError: string[] = [];
    addressError: string[] = [];
    contacts: Contact[] = [];
    selectedDocumentsButton: { [contactId: number]: number } = {};
    documentType: string = '';
    isUploadingPdf: boolean = false;
    countryConfigMap: Map<string, CountryFieldRequirement> = new Map();
    citizenshipCodes: CitizenshipCode[] = [];
    enforcementEnabled: boolean = true;

    constructor(private dialogRef: MatDialogRef<VerificationResultComponent>, private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: IdVerificationResponse,
        private idVerificationService: IdVerificationService,
        private documentService: UploadDocumentService,
        private snackBar: SnackbarService,
        private countryFieldRequirementService: CountryFieldRequirementService,
        private countryFieldValidationService: CountryFieldValidationService
    ) {
        dialogRef.disableClose = true;
    }

    ngOnInit(): void {
        this.data.contactVerificationResult.forEach(c => {
            if (c.errorMessage) {
                this.snackBar.errorSnackBar(c.errorMessage, 60000);
            }
        });

        this.countryFieldRequirementService.getConfig().subscribe(config => {
            this.countryConfigMap = new Map(config.countries.map(c => [c.countryCode.toUpperCase(), c]));
            this.citizenshipCodes = config.citizenshipCodes;
            this.enforcementEnabled = config.enforcementEnabled;
        });
    }

    contactPanelExpand(value: boolean) {
        this.contactPanelOpenState = value;
    }

    contactPanelClose(value: boolean) {
        this.contactPanelOpenState = value;
    }

    panelExpand(value: boolean) {
        this.panelOpenState = value;
    }

    panelClose(value: boolean) {
        this.panelOpenState = value;
    }

    async uploadPDF(auditId: number): Promise<void> {
        if (this.isUploadingPdf) return;

        this.isUploadingPdf = true;  
        this.showProgressBar = true;
        const OrgKey = sessionStorage.getItem(`actionStepOrgKey`);
        if (!OrgKey) {
            this.snackBar.warningSnackBar(
                'Org Key not found in the URL. Please navigate to the Id Verification Tool via Actionstep and try again.'
            );

            this.isUploadingPdf = false;
            this.showProgressBar = false;
            return;
        }
        try {
            const res = await firstValueFrom(
                this.documentService.uploadPdf(auditId, "idverification", OrgKey)
            );

            const path = res.meta.debug.server;
            const trimmedPath = path.replace(/app-ip-[^\.]+\./, '');
            const link = `https://${trimmedPath}/mym/asfw/workflow/documents/views/action_id/${res.actionDocuments.links.action}#mode=browse&view=list&folder=0&drive=DL`;

            const customSnackbarData: CustomSnackbarData = {
                message: `Document "${res.actionDocuments.name}" uploaded to matter no. #${res.actionDocuments.links.action}."`,
                link: link
            };

            this.snackBar.customSnackBar(customSnackbarData);

        } catch (err: any) {
            this.snackBar.errorSnackBar(err.error.errors[0].message);

            if (err.status === 400) {
                this.downloadPdfBtn = true;
            }
        } finally {

            this.isUploadingPdf = false;
            this.showProgressBar = false;
        }
    }

    downloadPDF(auditId: number) {
        this.showProgressBar = true;
        this.documentService.downlaodFile(auditId, "idverification").subscribe(file => {
            this.documentService.saveFile(file);
            this.showProgressBar = false;
        },
            (error: any) => {
                this.showProgressBar = false;
                this.snackBar.errorSnackBar("Failed to generate PDF.");
            }
        )
    }

    async verify(selectedContact: Contact, rowIndex: number) {
        if (!selectedContact.isCompany && selectedContact.role == null) {
            this.roleError = [];
            this.roleError[rowIndex] = "Please select role";
            return;
        }

        const result = this.countryFieldValidationService.validate(selectedContact, this.countryConfigMap, this.enforcementEnabled, this.citizenshipCodes);
        if (!result.isValid) {
            this.isUpdateAddress = rowIndex;
            this.addressError[rowIndex] = result.failures.map(f => f.message).join('; ');
            return;
        }

        if (selectedContact.street && selectedContact.street.length > 70) {
            this.isUpdateAddress = rowIndex;
            this.addressError[rowIndex] = "Street length exceeds the max limit of 70 characters.";
            return;
        }

        this.roleError = [];
        this.addressError = [];
        this.closeFields();
        this.showProgressBar = true;
        this.isVerifyDisabled = true;
        var selectedContacts: Contact[] = [];
        selectedContacts.push(selectedContact);
        await firstValueFrom(this.idVerificationService.runStrategy(selectedContacts)).then(res => {
            this.verificationResponse = res,
                this.isVerifyDisabled = false,
                this.showProgressBar = false;
            var dialogRef = this.dialog.open(VerificationResultComponent, {
                data: this.verificationResponse,
                height: '700px',
                maxHeight: '700px',
                width: '1400px',
                maxWidth: '1400px',
                panelClass: 'full-screen-modal'
            });
            dialogRef.afterClosed().subscribe(() => {
                selectedContact.role = null;
                selectedContact.country = '';
                selectedContact.selectedDocuments = [];
            });
        }
        ).catch(err => {
            this.isVerifyDisabled = false;
            this.showProgressBar = false;
            this.snackBar.errorSnackBar(err.error.errors[0].message);
        }
        );
    }

    createAddress(contact: Contact) {
        var address = [contact.houseName, contact.houseNumber, contact.street, contact.city, contact.cityDistrict, contact.state, contact.country, contact.postcode];
        return address.filter(c => c && c.length > 0).join(", ");
    }

    configFor(contact: Contact): CountryFieldRequirement | null {
        return this.countryConfigMap.get((contact.country ?? '').toUpperCase()) ?? null;
    }

    hasRuleOutcomes(contact: ContactVerificationResult): boolean {
        return (contact.verificationResponse?.rulesetOutcomes?.length ?? 0) > 0;
    }

    toggleEditMode(rowIndex: number): void {
        if (!this.editMode[rowIndex]) {
            this.editMode[rowIndex] = !this.editMode[rowIndex];
        }

    }

    // Check if the row is in edit mode
    isEditing(rowIndex: number): boolean {
        return this.editMode[rowIndex];
    }

    updateAddress(rowIndex: number) {
        if (this.isUpdateDob != null || this.isUpdateEmail != null) {
            this.isUpdateDob = null;
            this.isUpdateEmail = null;
        }
        this.isUpdateAddress = rowIndex;
        this.isAddressChecked = false;
    }
    saveAddress(event: any, rowIndex: number, contactIndex: number, houseNo: string, street: string, city: string, province: string, country: string, zipPostCode: string) {
        this.isAddressChecked = event.checked;
        if (event.checked) {
            this.isUpdateAddress = null;
            this.isAddressChecked = false;
            this.data.contactVerificationResult[contactIndex].ubos[rowIndex].houseNumber = houseNo;
            this.data.contactVerificationResult[contactIndex].ubos[rowIndex].street = street;
            this.data.contactVerificationResult[contactIndex].ubos[rowIndex].state = province;
            this.data.contactVerificationResult[contactIndex].ubos[rowIndex].city = city;
            this.data.contactVerificationResult[contactIndex].ubos[rowIndex].postcode = zipPostCode;
            this.data.contactVerificationResult[contactIndex].ubos[rowIndex].country = country;
            this.addressError = []
        }
    }

    updateDob(rowIndex: number) {
        if (this.isUpdateEmail != null || this.isUpdateAddress != null) {
            this.isUpdateEmail = null;
            this.isUpdateAddress = null;
        }
        this.isUpdateDob = rowIndex;
        this.isDobChecked = false;
    }

    saveDob(event: any, rowIndex: number, contactIndex: number, dob: string) {
        this.isDobChecked = event.checked;
        if (event.checked) {
            this.isUpdateDob = null;
            this.isDobChecked = false;
            const [month, day, year] = dob.split('/');
            const formattedDate = year + "-" + month.padStart(2, '0') + "-" + day.padStart(2, '0');
            this.data.contactVerificationResult[contactIndex].ubos[rowIndex].dateOfBirth = formattedDate;
        }
    }

    updateEmail(rowIndex: number) {
        if (this.isUpdateAddress != null || this.isUpdateDob != null) {
            this.isUpdateAddress = null;
            this.isUpdateDob = null;
        }
        this.isUpdateEmail = rowIndex;
        this.isEmailChecked = false;
    }

    saveEmail(event: any, rowIndex: number, contactIndex: number, email: string) {
        this.isEmailChecked = event.checked;

        if (event.checked) {
            this.isUpdateEmail = null;
            this.data.contactVerificationResult[contactIndex].ubos[rowIndex].email = email;
        }
    }


    validateEmailInput(email: string, rowIndex: number): boolean {
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (emailPattern.test(email)) {
            this.emailError = [];
            return false
        }
        else {
            this.emailError[rowIndex] = "Enter valid email";
            return true
        }
    }

    onRoleChange(event: MatSelectChange, rowIndex: number, contactIndex: number,) {
        this.roleError = [];
        this.data.contactVerificationResult[contactIndex].ubos[rowIndex].role = event.value;
    }

    closeFields() {
        this.isUpdateAddress = null;
        this.isUpdateEmail = null;
        this.isUpdateDob = null;
    }
    removeDocument(index: number, contactIndex: number, doc: any): void {
        const contact = this.data.contactVerificationResult[contactIndex].ubos[index];

        if (contact && contact.selectedDocuments) {
            // Remove the selected document from the list
            contact.selectedDocuments = contact.selectedDocuments.filter(d => d !== doc);

            // Reset button state if no documents are left
            if (contact.selectedDocuments.length === 0) {
                this.selectedDocumentsButton[index] = 0;
            }

            // Update the data model
            this.data.contactVerificationResult[contactIndex].ubos[index].selectedDocuments = contact.selectedDocuments;

            // Trigger change detection if necessary
            this.cdr.detectChanges();
        }
    }

    onButtonClick(index: number, contactIndex: number, buttonId: number): void {
        const matterNo = sessionStorage.getItem("matterId");
        const contact = this.contacts.find(c => c.contactId === index);
        this.data.contactVerificationResult[contactIndex].ubos
        const selectedDocs = this.data.contactVerificationResult[contactIndex].ubos?.find(ubo => ubo.contactId === index)?.selectedDocuments || [];

        const documentType = buttonId === 1 ? 'POI' : 'POA';

        const dialogRef = this.dialog.open(DocumentListComponent, {
            maxWidth: '50vw',
            width: '90%',
            panelClass: 'responsive-dialog',
            data: { actionId: matterNo, selectedDocuments: selectedDocs, documentType: documentType, country: contact?.country }
        });

        dialogRef.afterClosed().subscribe((returnValues: ActionDocuments[]) => {
            if (returnValues) {
                const contact = this.data.contactVerificationResult[contactIndex].ubos[index];
                if (contact) {
                    this.selectedDocumentsButton[index] = this.selectedDocumentsButton[index] || 0;
                    this.selectedDocumentsButton[index] = (this.selectedDocumentsButton[index] % 2) + 1;
                    // Update selected documents based on the button clicked
                    if (buttonId === 1) {
                        contact.selectedDocuments = contact.selectedDocuments || [];
                        // Assign documentType as POI
                        returnValues.forEach(doc => doc.documentType = 'POI');
                        contact.selectedDocuments = [...contact.selectedDocuments, ...returnValues];
                    }
                    else if (buttonId === 2) {
                        contact.selectedDocuments = contact.selectedDocuments || [];
                        // Assign documentType as POA
                        returnValues.forEach(doc => doc.documentType = 'POA');
                        contact.selectedDocuments = [...contact.selectedDocuments, ...returnValues];
                    }
                    this.data.contactVerificationResult[contactIndex].ubos[index].selectedDocuments = contact.selectedDocuments;
                }
            }
        });
    }

    isPOASelected(): boolean {
        return this.documentType === 'POA';
    }

    getdocumentsbytype(documents: ActionDocuments[], type: string) {
        return documents.filter(d => d.documentType == type);
    }
}
